import { useState } from 'react'
import PublicLayout from '@/components/layout/PublicLayout'

export default function Feedback({ embedded }: { embedded?: boolean }) {
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    setStatus('sending')
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          email: email.trim() || undefined,
        }),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Request failed' }))
        throw new Error(data.error || 'Request failed')
      }
      setStatus('sent')
      setMessage('')
      setEmail('')
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong')
    }
  }

  const Wrapper = embedded ? ({ children }: { children: React.ReactNode }) => <>{children}</> : PublicLayout

  return (
    <Wrapper>
      <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
        <div className="w-full max-w-xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <svg className="w-12 h-12 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
              </svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Feedback</h1>
            <p className="text-[var(--text-secondary)] mt-2">
              Bug reports, feature requests, or just a kind word — we'd love to hear from you.
            </p>
          </div>

          {status === 'sent' ? (
            <div className="card text-center">
              <svg className="w-12 h-12 text-[var(--bac-safe)] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Thank you!</h2>
              <p className="text-[var(--text-secondary)]">Your feedback has been received. We appreciate you taking the time.</p>
              <button
                onClick={() => setStatus('idle')}
                className="btn btn-secondary mt-4"
              >
                Send more feedback
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                  Your feedback
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What's on your mind?"
                  rows={5}
                  required
                  className="w-full resize-y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                  Email <span className="text-[var(--text-secondary)] opacity-60">(optional — only if you'd like a reply)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full"
                />
              </div>

              {status === 'error' && (
                <p className="text-sm text-[var(--bac-danger)]">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === 'sending' || !message.trim()}
                className="btn btn-primary w-full"
              >
                {status === 'sending' ? 'Sending...' : 'Send Feedback'}
              </button>

              <p className="text-xs text-[var(--text-secondary)] text-center">
                Your feedback is anonymous unless you choose to include your email.
              </p>
            </form>
          )}
        </div>
      </div>
    </Wrapper>
  )
}
